import { useQuery } from '@tanstack/react-query';
import { RegisterScreen } from '@/components/RegisterScreen';
import { lineNumber, type FormLine, type FormValues } from '@/components/ChildRecords';
import { formatCurrency, formatDate, formatNumber, todayIso } from '@/components/live';
import { partyOptions } from '@/api/lookups';
import { usePickers } from '@/hooks/usePickers';
import { useRows } from '@/hooks/useResource';
import { isSupabaseConfigured } from '@/lib/supabase';
import { PLANTING_STOCK_TYPES, SAPLING_SOURCES, optionLabel } from '@/lib/practiceLists';

interface Row {
  id: string; sapling_issue_code: string | null;
  farmer_name: string; farmer_code: string | null; plot_code: string | null; village_name: string | null;
  species_label: string | null; issued_on: string; quantity_issued: number;
  stock_source: string; nursery_name: string | null; batch_no: string | null;
  non_gmo_declaration_ref: string | null; planting_stock_type: string | null; age_months: number | null;
  is_free: boolean; rate_per_sapling: number | null; total_amount: number | null;
  unit_value: number | null; stock_value: number | null;
  expected_planting_on: string | null; received_by_name: string | null;
  harvest_id: string | null; harvest_code: string | null;
}

/**
 * Planting stock handed to a farmer (owner, 2026-09-25: "Add a sapling issue record").
 *
 * Nothing recorded stock leaving the organisation. The plot registers say what was PLANTED, and
 * the tree inventory counts what is standing, but neither knew what was handed over, of what
 * species, from which nursery, or whether the non-GMO declaration for that batch was held - which
 * is what FSC 10.4 asks, and what annual monitoring indicator 10.4.1 has been asking with nothing
 * behind it to point at.
 *
 * It is also the missing half of a number already on screen: the inventory counts survivors, and
 * without the issue there is nothing to count them against. `Sapling survival` does that.
 */
export default function SaplingIssuesScreen() {
  const pick = usePickers(['farmers', 'plots', 'species']);
  // Nurseries are PARTIES (owner: "nursery will be under party"), under the `nursery` role added
  // with this register. Somebody you buy stock from has KYC and contact details like any supplier.
  const nurseries = useQuery({
    queryKey: ['picker', 'parties', 'nursery'],
    queryFn: () => partyOptions('nursery'),
    enabled: isSupabaseConfigured,
  });
  const fromSomebody = (form: Record<string, unknown>) =>
    ['external_nursery', 'forest_department'].includes(String(form.stock_source));
  const charged = (form: FormValues) => !form.is_free;
  const money = (value: number | null) => (value === null ? '' : value.toFixed(2));
  // What the farmer pays per sapling: the rate typed, else the stock's own value.
  const rateOf = (line: FormLine) => lineNumber(line.rate_per_sapling) ?? lineNumber(line.unit_value);
  const times = (a: number | null, b: number | null) => (a === null || b === null ? null : a * b);
  const lines = (form: FormValues) => (Array.isArray(form.species_lines) ? form.species_lines as FormLine[] : []);
  // Restocking a harvest (owner, 2026-09-27: "harvest report follows regeneration aspect associated
  // for seedling distribution"): the farmer's harvests that regenerate by planting.
  const harvests = useRows<{ id: string; harvest_code: string | null; farmer_id: string; plot_id: string; plot_code: string | null;
                             harvest_date: string; regeneration_method: string | null }>('plot_harvest_entries', { orderBy: { column: 'harvest_date', ascending: false } });

  return (
    <RegisterScreen<Row>
      addLabel="Record an issue" exportName="sapling-issues"
      orderBy={{ column: 'issued_on', ascending: false }} recordNoun="issue"
      recordLabel={(row) => `sapling issue ${row.sapling_issue_code ?? ''} - ${row.quantity_issued} to ${row.farmer_name}`}
      screenKey="plot.inventory"
      searchColumns={['sapling_issue_code', 'farmer_name', 'farmer_code', 'plot_code', 'batch_no', 'nursery_name']}
      secondary={{ sum: (row) => row.quantity_issued, unit: 'saplings', digits: 0 }}
      subtitle="Planting stock handed over: how many, of what, from which nursery, and under whose non-GMO declaration."
      table="ops.sapling_issues" title="Sapling issue" view="sapling_issues"
      columns={[
        { key: 'sapling_issue_code', label: 'Ref', render: (row) => <strong>{row.sapling_issue_code ?? '--'}</strong> },
        { key: 'farmer_name', label: 'Farmer', render: (row) => (
          <span className="cell-stack"><strong>{row.farmer_name}</strong>
            <small className="muted">{[row.farmer_code, row.plot_code, row.village_name].filter(Boolean).join(' · ')}</small></span>
        ) },
        { key: 'species_label', label: 'Species', render: (row) => (
          <span className="cell-stack">{row.species_label ?? '--'}{row.harvest_code && <small className="muted">restocks {row.harvest_code}</small>}</span>
        ) },
        { key: 'quantity_issued', label: 'Saplings', align: 'right', render: (row) => formatNumber(row.quantity_issued, 0) },
        { key: 'issued_on', label: 'Issued', render: (row) => formatDate(row.issued_on) },
        { key: 'stock_source', label: 'Source', render: (row) => (
          <span className="cell-stack"><strong>{optionLabel(SAPLING_SOURCES, row.stock_source)}</strong>
            <small className="muted">{[row.nursery_name, row.batch_no].filter(Boolean).join(' · ')}</small></span>
        ) },
        { key: 'non_gmo_declaration_ref', label: 'Non-GMO', render: (row) => (
          row.non_gmo_declaration_ref ?? <span className="field-error">none on file</span>
        ) },
        { key: 'stock_value', label: 'Stock value', align: 'right',
          render: (row) => (row.stock_value === null ? <span className="muted">--</span> : formatCurrency(row.stock_value)) },
        { key: 'total_amount', label: 'Charged', align: 'right',
          render: (row) => (row.is_free ? <span className="muted">Free</span> : formatCurrency(row.total_amount)) },
      ]}
      stats={[
        // FSC 10.4 asks for the declaration on the batch. An issue without one is the finding.
        { key: 'no_gmo', label: 'No non-GMO declaration', tone: 'error', match: (row) => !row.non_gmo_declaration_ref },
        { key: 'free', label: 'Issued free', tone: 'ok', match: (row) => row.is_free },
        { key: 'external', label: 'From outside', tone: 'info',
          match: (row) => ['external_nursery', 'forest_department'].includes(row.stock_source) },
        { key: 'restock', label: 'Restocking a harvest', tone: 'info', match: (row) => Boolean(row.harvest_id) },
        { key: 'unplanted', label: 'Planting date passed', tone: 'warn',
          match: (row) => Boolean(row.expected_planting_on) && row.expected_planting_on! < todayIso() },
      ]}
      defaults={{ issued_on: todayIso(), stock_source: 'own_nursery', is_free: true }}
      fields={[
        { key: 'farmer_id', label: 'Farmer', type: 'select', options: pick.farmers, required: true },
        { key: 'plot_id', label: 'Plot (optional)', type: 'select', options: pick.plots,
          hint: 'Leave blank when the stock is issued to the farmer rather than for one plot.' },
        { key: 'harvest_id', label: 'Restocks harvest', type: 'select',
          optionsFor: (form) => (harvests.data ?? []).filter((row) => row.farmer_id === form.farmer_id)
            .map((row) => ({ id: row.id, label: `${row.harvest_code ?? 'Harvest'} - ${row.plot_code ?? ''}, ${formatDate(row.harvest_date)}${
              row.regeneration_method === 'replanting' || row.regeneration_method === 'assisted_natural' ? ' (replanting planned)' : ''}` })),
          placeholderFor: (form) => (form.farmer_id ? 'Not for a harvest' : 'Choose the farmer first'),
          hint: 'For post-harvest replanting: the Harvest report counts these saplings against the plan.' },
        { key: 'issued_on', label: 'Issued on', type: 'date', required: true, max: todayIso() },
        { key: 'expected_planting_on', label: 'To be planted by', type: 'date' },
        // One line per species (owner, 2026-09-27: "can have the value per species capture?",
        // "bring in table format?"). A handover of three species saves as three issues.
        { key: 'is_free', label: 'Issued free', type: 'check', placeholder: 'No charge to the farmer', section: 'Species & value' },
        { key: 'species_lines', label: 'Species issued', type: 'lines', required: true, addLineLabel: 'Add species',
          hint: 'Value is what a sapling of that stock is worth, charged or not: a free issue is still stock with a value.',
          columns: [
            { key: 'species_id', label: 'Species', type: 'select', options: pick.species, required: true, addTo: 'species', minWidth: 190 },
            { key: 'planting_stock_type', label: 'Stock type', type: 'select', options: PLANTING_STOCK_TYPES, minWidth: 120 },
            { key: 'age_months', label: 'Age (months)', type: 'number' },
            { key: 'quantity_issued', label: 'Saplings', type: 'number', required: true, total: true },
            { key: 'unit_value', label: 'Value / sapling (₹)', type: 'number' },
            { key: 'stock_value', label: 'Stock value (₹)', type: 'computed', total: true,
              compute: (line) => money(times(lineNumber(line.quantity_issued), lineNumber(line.unit_value))) },
            { key: 'rate_per_sapling', label: 'Charged / sapling (₹)', type: 'number', showIf: charged,
              compute: (line) => money(lineNumber(line.unit_value)) },
            { key: 'total_amount', label: 'Charged (₹)', type: 'computed', save: true, total: true, showIf: charged,
              compute: (line) => money(times(lineNumber(line.quantity_issued), rateOf(line))) },
          ] },
        { key: 'stock_source', label: 'Source', type: 'select', options: SAPLING_SOURCES, required: true, section: 'Stock' },
        { key: 'nursery_party_id', label: 'Nursery', type: 'select', options: nurseries.data ?? [], required: fromSomebody,
          placeholder: (nurseries.data ?? []).length ? 'Select the nursery' : 'No nursery is registered as a party yet',
          hint: 'Nurseries are parties. Add one under Stakeholders with the nursery role.',
          showIf: fromSomebody },
        { key: 'batch_no', label: 'Batch no.', type: 'text', placeholder: 'e.g. B-2026-14' },
        { key: 'non_gmo_declaration_ref', label: 'Non-GMO declaration ref.', type: 'text',
          placeholder: 'e.g. NGMO/2026/0071',
          hint: 'FSC 10.4: the declaration is held per batch of planting stock.' },
        { key: 'received_by_name', label: 'Received by', type: 'text', section: 'Handover',
          hint: 'When somebody other than the farmer collected them.' },
        { key: 'document_id', label: 'Issue slip', type: 'file' },
        { key: 'remarks', label: 'Remarks', type: 'textarea' },
      ]}
      validate={(form) => ({
        species_lines: lines(form).some((line) => line.quantity_issued && !(Number(line.quantity_issued) > 0))
          ? 'Every line issues more than zero saplings.' : undefined,
        expected_planting_on: form.expected_planting_on && String(form.expected_planting_on) < String(form.issued_on ?? '')
          ? 'They cannot be planted before they were issued.' : undefined,
      })}
    />
  );
}
